import {
    Box,
    Typography,
    Card,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Button,
} from '@mui/material'
import { OpenInNew, Receipt, ReceiptLong } from '@mui/icons-material'
import { formatCurrency, isSupportedCurrency } from '@yourownmetric/shared'

import type { InvoiceData } from '@/apis/billingApi'

interface InvoiceHistoryTabProps {
    invoices: InvoiceData[]
}

export function InvoiceHistoryTab({ invoices }: InvoiceHistoryTabProps) {
    if (invoices.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 8,
                    color: 'text.secondary',
                }}
            >
                <ReceiptLong sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
                <Typography variant="h6" color="text.secondary">
                    No invoices yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Invoices will appear here once your first billing cycle completes.
                </Typography>
            </Box>
        )
    }

    return (
        <>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt sx={{ fontSize: 20 }} />
                Invoice History
            </Typography>
            <Card>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Invoice #</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoices.map(inv => (
                            <TableRow key={inv.id}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={500}>
                                        {inv.invoiceNumber ?? '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {new Date(inv.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    {isSupportedCurrency(inv.currency)
                                        ? formatCurrency(inv.amountCents, inv.currency)
                                        : `${inv.currency.toUpperCase()} ${(inv.amountCents / 100).toFixed(2)}`}
                                    {inv.taxCents != null && inv.taxCents > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block' }}
                                        >
                                            incl. {inv.taxLabel ?? 'tax'}{' '}
                                            {isSupportedCurrency(inv.currency)
                                                ? formatCurrency(inv.taxCents, inv.currency)
                                                : `${inv.currency.toUpperCase()} ${(inv.taxCents / 100).toFixed(2)}`}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={inv.status}
                                        size="small"
                                        color={inv.status === 'paid' ? 'success' : 'default'}
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {inv.periodStart
                                        ? `${new Date(inv.periodStart).toLocaleDateString()} – ${new Date(inv.periodEnd!).toLocaleDateString()}`
                                        : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {inv.hostedUrl && (
                                        <Button
                                            size="small"
                                            href={inv.hostedUrl}
                                            target="_blank"
                                            startIcon={<OpenInNew />}
                                        >
                                            View
                                        </Button>
                                    )}
                                    {inv.pdfUrl && (
                                        <Button size="small" href={inv.pdfUrl} target="_blank">
                                            PDF
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </>
    )
}
